/**
 * Adapated from Uday Vunnam's article regarding LRU cache.
 *
 * Originally, a standard JS object was used for the cache; this has
 * been upted to utilize a JS Map object, which keeps insertion order.
 *
 * @see: https://dev.to/udayvunnam/implementing-lru-cache-in-javascript-3c8g
 */
import 'regenerator-runtime/runtime';

class Node {
  key: string;
  value: unknown;
  next: Node | null;
  prev: Node | null;

  constructor(key: string, value: unknown, next: Node | null = null, prev: Node | null = null) {
    this.key = key;
    this.value = value;
    this.next = next;
    this.prev = prev;
  }
}

class LRU {
  private size: number;
  private limit: number;
  private head: Node | null;
  private tail: Node | null;
  private cache: Map<string, Node>;

  //set default limit of 10 if limit is not passed.
  constructor(limit = 10) {
    this.size = 0;
    this.limit = limit;
    this.head = null;
    this.tail = null;
    this.cache = new Map();
  }

  // Write Node to head of LinkedList
  // update cache with Node key and Node reference
  write(key: string, value: unknown): void {
    this.ensureLimit();

    if (!this.head) {
      this.head = this.tail = new Node(key, value);
    } else {
      const node = new Node(key, value, this.head);
      this.head.prev = node;
      this.head = node;
    }

    //Update the cache map
    this.cache.set(key, this.head);
    this.size++;
  }

  // Read from cache map and make that node as new Head of LinkedList
  read(key: string): unknown | null {
    if (this.cache.get(key)) {
      const value = this.cache.get(key)!.value;

      // node removed from it's position and cache
      this.remove(key);
      // write node again to the head of LinkedList to make it most recently used
      this.write(key, value);

      return value;
    }

    return null;
  }

  private ensureLimit(): void {
    if (this.size === this.limit) {
      this.remove(this.tail!.key);
    }
  }

  remove(key: string): void {
    const node = this.cache.get(key);
    if (!node) return;

    if (node.prev !== null) {
      node.prev.next = node.next;
    } else {
      this.head = node.next;
    }

    if (node.next !== null) {
      node.next.prev = node.prev;
    } else {
      this.tail = node.prev;
    }

    this.cache.delete(key);
    this.size--;
  }

  clear(): void {
    this.head = null;
    this.tail = null;
    this.size = 0;
    this.cache = new Map();
  }

  getSize(): number {
    return this.size;
  }

  // Invokes the callback function with every node of the chain and the index of the node.
  forEach(fn: (node: Node, index: number) => void): void {
    let node = this.head;
    let counter = 0;
    while (node) {
      fn(node, counter);
      node = node.next;
      counter++;
    }
  }

  // To iterate over LRU with a 'for...of' loop
  *[Symbol.iterator](): Generator<Node> {
    let node = this.head;
    while (node) {
      yield node;
      node = node.next;
    }
  }
}

export default LRU;
